package com.evoapps.evoshift.data.repository

import com.evoapps.evoshift.data.remote.FirebaseStorageClient
import com.evoapps.evoshift.domain.repository.ProfilePhotoRepository
import com.evoapps.evoshift.data.remote.AppFirestore
import io.ktor.client.HttpClient
import io.ktor.client.request.get
import io.ktor.client.statement.bodyAsBytes
import io.ktor.http.URLProtocol
import io.ktor.http.Url
import io.ktor.http.isSuccess

/**
 * Profile photos (SH-94): the file in Storage (`users/{uid}/avatar.jpg`) through
 * [FirebaseStorageClient], the link in `users/{uid}.photoUrl` through Firestore.
 */
class ProfilePhotoRepositoryImpl(
    private val firestore: AppFirestore,
    private val storage: FirebaseStorageClient,
    private val httpClient: HttpClient
) : ProfilePhotoRepository {

    override suspend fun upload(uid: String, jpeg: ByteArray, onProgress: (Float) -> Unit): Result<String> = runCatching {
        val path = avatarPath(uid)
        val generation = storage.upload(path, jpeg, CONTENT_TYPE, onProgress)
        storage.downloadUrl(path, generation)
    }

    override suspend fun delete(uid: String): Result<Unit> = runCatching { storage.delete(avatarPath(uid)) }

    override suspend fun setPhotoUrl(uid: String, photoUrl: String?): Result<Unit> = runCatching {
        firestore.collection("users").document(uid).updateFields { "photoUrl" to photoUrl }
    }

    override suspend fun download(url: String): Result<ByteArray> = runCatching {
        when {
            storage.isOwnUrl(url) -> storage.download(url)
            isGooglePhoto(url) -> {
                val response = httpClient.get(url)
                check(response.status.isSuccess()) { "Photo download failed with HTTP ${response.status.value}" }
                response.bodyAsBytes()
            }
            else -> throw IllegalArgumentException("Photo address not allowed")
        }
    }

    companion object {
        const val CONTENT_TYPE = "image/jpeg"

        fun avatarPath(uid: String): String {
            require(uid.isNotBlank() && '/' !in uid) { "Invalid uid" }
            return "users/$uid/avatar.jpg"
        }

        /** A Google account photo (set at sign-up with Google), served over HTTPS by Google. */
        fun isGooglePhoto(url: String): Boolean {
            val parsed = runCatching { Url(url) }.getOrNull() ?: return false
            return parsed.protocol == URLProtocol.HTTPS &&
                (parsed.host == "googleusercontent.com" || parsed.host.endsWith(".googleusercontent.com"))
        }
    }
}
